import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { IconArrowLeft, IconArrowUp, IconChevronRight, IconDownload } from "@tabler/icons-react";
import { agreementEdition, agreementIntro, agreementPlainText, agreementSections } from "../features/shop/user-agreement.js";
import "./UserAgreementPage.css";

export function UserAgreementPage({ onClose }) {
  const root = useRef(null);
  const back = useRef(null);
  useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    back.current?.focus();
    const handleKey = event => {
      if (event.key === "Escape") onClose();
      if (event.key !== "Tab") return;
      const elements = root.current?.querySelectorAll("button, a[href]");
      if (!elements?.length) return;
      const first = elements[0], last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKey);
      previousFocus?.focus?.({ preventScroll: true });
    };
  }, [onClose]);

  const download = () => {
    const url = URL.createObjectURL(new Blob([agreementPlainText()], { type: "text/plain;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "MetaCard-user-agreement.txt";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return createPortal(<div ref={root} className="mc-agreement-screen" role="dialog" aria-modal="true" aria-labelledby="mc-agreement-title">
    <div className="mc-agreement-page">
      <header className="mc-agreement-toolbar">
        <button ref={back} type="button" onClick={onClose} aria-label="Вернуться в магазин"><IconArrowLeft size={20} /></button>
        <span>MetaCard · документы</span>
        <button type="button" onClick={download} aria-label="Скачать полный текст соглашения"><IconDownload size={20} /></button>
      </header>
      <div className="mc-agreement-hero">
        <h1 id="mc-agreement-title">Пользовательское соглашение</h1>
        <p className="mc-agreement-edition">Редакция от {agreementEdition}</p>
        <p className="mc-agreement-intro">{agreementIntro}</p>
      </div>
      <nav className="mc-agreement-contents" aria-label="Содержание соглашения">
        <h2>Содержание</h2>
        {agreementSections.map((section, index) => <a key={section.id} href={`#mc-agreement-${section.id}`} onClick={event => {
          event.preventDefault();
          root.current?.querySelector(`#mc-agreement-${section.id}`)?.scrollIntoView({ block: "start", behavior: "instant" });
        }}><span>{String(index + 1).padStart(2, "0")}</span><b>{section.title}</b><IconChevronRight size={16} /></a>)}
      </nav>
      <article className="mc-agreement-document">
        {agreementSections.map((section, index) => <section key={section.id} id={`mc-agreement-${section.id}`}>
          <h2><span>{index + 1}.</span>{section.title}</h2>
          {section.paragraphs.map((paragraph, item) => <p key={item}><span className="mc-agreement-clause">{index + 1}.{item + 1}.</span> {paragraph}</p>)}
        </section>)}
      </article>
      <button className="mc-agreement-top" type="button" onClick={() => root.current?.scrollTo({ top: 0, behavior: "instant" })} aria-label="К началу соглашения"><IconArrowUp size={20} /></button>
    </div>
  </div>, document.body);
}
